import { createContext, useContext, useEffect } from 'react';
import { v4 } from 'uuid';

import {
  SOURCE_CHECKOUT,
  X_SEEK_SITE_AD_BUDGET_PURCHASE,
} from 'src/App/constants/httpHeaders';
import { useAppContext } from 'src/App/context/AppContext';
import { useCreateOrderWithPromotionMutation } from 'src/App/generated/graphql-types';
import { useProductSet } from 'src/App/pages/checkout/context/ProductSetProvider';
import { AppError } from 'src/App/utils/error';
import { getLogger } from 'src/App/utils/logger/logger';

import { useQueryAccountInfo } from '../../../hooks/useQueryAccountInfo.ts';

import { useStore } from './provider';
import { CheckoutActionType } from './reducer';

interface CreateOrderProviderProps {}
interface CreateOrderContextProps {
  orderId: string;
}

const CreateOrderContext = createContext<CreateOrderContextProps | null>(null);

export const CreateOrderProvider: React.FC<
  React.PropsWithChildren<CreateOrderProviderProps>
> = ({ children }) => {
  const { dispatch } = useStore();
  const { accountInfo } = useQueryAccountInfo();
  const advertiserId = accountInfo?.currentAdvertiser?.advertiserId ?? '';
  const { country } = useAppContext();

  const { availableProductSetId, availableProductId } = useProductSet();

  const [
    createOrderWithPromotionMutation,
    {
      data: createdOrderWithPromotionData,
      error: createOrderWithPromotionError,
    },
  ] = useCreateOrderWithPromotionMutation({
    context: {
      headers: {
        'x-seek-site': X_SEEK_SITE_AD_BUDGET_PURCHASE,
        source: SOURCE_CHECKOUT,
        site: country,
      },
    },
    onError: (error) => {
      getLogger().error('create order error', {
        error,
        activity: 'createOrder',
        productSetId: availableProductSetId,
        productId: availableProductId,
      });
    },
    onCompleted: (data) => {
      if (
        data.createOrderWithPromotion?.__typename ===
          'CreateOrderSuccessPayload' &&
        data.createOrderWithPromotion.order
      ) {
        dispatch({
          type: CheckoutActionType.SET_CREATED_ORDER,
          orderId: data.createOrderWithPromotion.order!.orderId,
          purchasePaymentReferenceNumber:
            data.createOrderWithPromotion.order!.orderNumber,
          productSetId: availableProductSetId,
          idempotencyKey: v4(),
        });
      }
    },
  });

  useEffect(() => {
    if (
      advertiserId &&
      country &&
      availableProductSetId &&
      availableProductId
    ) {
      createOrderWithPromotionMutation({
        variables: {
          input: {
            advertiserId,
            advertiserNation: country,
            lineItems: [
              {
                productAttributes: {
                  catalogueProduct: {
                    productId: availableProductId,
                    productSetId: availableProductSetId,
                  },
                },
                productType: 'CATALOGUE_PRODUCT',
              },
            ],
            orderCreationId: v4(),
          },
        },
      });
    }
  }, [
    advertiserId,
    country,
    availableProductId,
    availableProductSetId,
    createOrderWithPromotionMutation,
  ]);

  if (createOrderWithPromotionError) {
    throw new AppError(
      'CREATE_ORDER_ERROR',
      createOrderWithPromotionError.message,
    );
  }

  if (
    createdOrderWithPromotionData?.createOrderWithPromotion?.__typename ===
    'PromotionInvalidError'
  ) {
    throw new AppError('CREATE_ORDER_ERROR', 'promo code expired');
  }

  const orderId =
    createdOrderWithPromotionData?.createOrderWithPromotion?.__typename ===
    'CreateOrderSuccessPayload'
      ? createdOrderWithPromotionData.createOrderWithPromotion.order?.orderId
      : undefined;

  if (
    createdOrderWithPromotionData?.createOrderWithPromotion?.__typename ===
      'CreateOrderSuccessPayload' &&
    !orderId
  ) {
    throw new AppError('CREATE_ORDER_ERROR', 'unexpected undefined orderId');
  }

  return (
    <CreateOrderContext.Provider value={{ orderId: orderId ?? '' }}>
      {children}
    </CreateOrderContext.Provider>
  );
};

export const useCreateOrder = () => {
  const context = useContext(CreateOrderContext);
  if (context === null) {
    throw new Error('useCreateOrder must be used within a CreateOrderProvider');
  }

  return context;
};
